import {
  SimpleGrid,
  Box,
  Select,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';

import SectionCard from '../../components/SectionCard';
import ValidatedField from '../../components/ValidatedField';
import { useAnswerCodes } from '../../hooks';
import type { Location } from 'api';

interface ProtectionClassCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
}

const UNPROTECTED_HYDRANT_CODES = ['508767', '508768'];
const UNPROTECTED_FIREHALL_KM = 8;

const ProtectionClassCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
}: ProtectionClassCardProps) => {
  const { getOptions } = useAnswerCodes();
  const hydrantOptions = getOptions('metres_to_hydrant');

  const handleHydrantChange = (value: string) => {
    const shouldBeUnprotected =
      UNPROTECTED_HYDRANT_CODES.includes(value) ||
      (location.kilometres_to_firehall !== null &&
        location.kilometres_to_firehall > UNPROTECTED_FIREHALL_KM);

    onUpdate(locationIndex, 'metres_to_hydrant', value);
    if (shouldBeUnprotected !== location.unprotected) {
      onUpdate(locationIndex, 'unprotected', shouldBeUnprotected);
    }
  };

  const handleFirehallChange = (valueString: string) => {
    const value = valueString ? parseFloat(valueString) : null;
    const shouldBeUnprotected =
      (value !== null && value > UNPROTECTED_FIREHALL_KM) ||
      UNPROTECTED_HYDRANT_CODES.includes(location.metres_to_hydrant || '');

    onUpdate(locationIndex, 'kilometres_to_firehall', value);
    if (shouldBeUnprotected !== location.unprotected) {
      onUpdate(locationIndex, 'unprotected', shouldBeUnprotected);
    }
  };

  return (
    <SectionCard title="Protection Class">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <Box>
          <ValidatedField
            itemPath="location"
            fieldPath="metres_to_hydrant"
            label="Metres To Hydrant"
            entity={location as unknown as Record<string, unknown>}
            isDisabled={isUpdating}
          >
            <Select
              variant="main"
              size="sm"
              value={location.metres_to_hydrant || ''}
              onChange={(e) => handleHydrantChange(e.target.value)}
              isDisabled={isUpdating}
            >
              {hydrantOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>
        </Box>

        <Box>
          <ValidatedField
            itemPath="location"
            fieldPath="kilometres_to_firehall"
            label="Kilometres to Firehall"
            entity={location as unknown as Record<string, unknown>}
            isDisabled={isUpdating}
          >
            <NumberInput
              variant="main"
              value={location.kilometres_to_firehall ?? ''}
              onChange={handleFirehallChange}
              isDisabled={isUpdating}
              min={0}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
          </ValidatedField>
        </Box>
      </SimpleGrid>
    </SectionCard>
  );
};

export default ProtectionClassCard;
